// ✅ src/pages/VehicleV2/VehicleTransferApproval.jsx  (FULL UPDATED FILE)

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaFilter,
  FaEye,
  FaSyncAlt,
  FaUndo,
  FaArrowLeft,
  FaCheck,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/vehicleTransferApproval";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const normalizeSelect = (v, fallback = "Select") => {
  const s = clean(v);
  return s || fallback;
};

const defaultForm = {
  vehicleMainType: "Select",
  store: "Select",
  vehicleCode: "Select",
  vehicleRegNo: "Select",
  status: "Select",
  month: "Select",
  year: "Select",
  transferTo: "Select",
  transferFromLoc: "Select",
  transferToLoc: "Select",
};

const VehicleTransferApproval = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- shared local/session storage pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(true);

  const [filterType, setFilterType] = useState("own"); // own | spares
  const [transferType, setTransferType] = useState("branch"); // branch | location

  const [form, setForm] = useState(defaultForm);

  const [init, setInit] = useState({
    years: ["Select"],
    months: ["Select"],
    statusOptions: ["Select"],
    stores: [],
    vehicleTypes: [],
    branches: [],
    fromLocations: [],
    toLocations: [],
  });

  const [vehicleCodeDD, setVehicleCodeDD] = useState([]);
  const [vehicleRegNoDD, setVehicleRegNoDD] = useState([]);

  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState({});
  const [approvedBy, setApprovedBy] = useState({});

  const isBusy =
    pageLoading || dropdownLoading || gridLoading || approveLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const getTransferApiType = useCallback(
    () => (transferType === "branch" ? "B" : "L"),
    [transferType]
  );

  const getRowKey = useCallback((r, idx) => {
    return String(
      r?.fld_vehicle_trandferid ??
        r?.fld_spare_trandferid ??
        r?.fld_vehicle_transferid ??
        r?.vehicleTransferId ??
        r?.spareTransferId ??
        r?.id ??
        idx
    );
  }, []);

  const isApprovedRow = useCallback((r) => {
    const v =
      r?.fld_vehicle_isapproved ??
      r?.vehicle_isapproved ??
      r?.isapproved ??
      r?.isApproved;
    return String(v).toLowerCase() === "true" || String(v) === "1";
  }, []);

  const col = useCallback((r, ...keys) => {
    for (const k of keys) {
      if (r?.[k] !== undefined && r?.[k] !== null && String(r[k]).trim() !== "") {
        return r[k];
      }
    }
    return "";
  }, []);

  const buildVehicleDropdownParams = useCallback(
    () => ({
      type: getTransferApiType(),
      branchId: branchType === "HO" ? "%" : branchId,
      branchType,
      status: normalizeSelect(form.status),
      store: normalizeSelect(form.store),
      loginUserId,
    }),
    [getTransferApiType, branchId, branchType, form.status, form.store, loginUserId]
  );

  const buildListParams = useCallback(
    (activeForm, activeFilterType = filterType, activeTransferType = transferType) => {
      const type = activeTransferType === "branch" ? "B" : "L";

      const params = {
        branch: branchType,
        type,
        branchId,
        branchType,
        status: normalizeSelect(activeForm.status),
        transferTo: normalizeSelect(activeForm.transferTo),
        fromLoc:
          activeTransferType === "location"
            ? normalizeSelect(activeForm.transferFromLoc)
            : "Select",
        toLoc:
          activeTransferType === "location"
            ? normalizeSelect(activeForm.transferToLoc)
            : "Select",
        vehicleCode: normalizeSelect(activeForm.vehicleCode),
        vehicleRegNo: normalizeSelect(activeForm.vehicleRegNo),
        store: normalizeSelect(activeForm.store),
        loginUserId,
        month: normalizeSelect(activeForm.month),
        year: normalizeSelect(activeForm.year),
        vehicletype: normalizeSelect(activeForm.vehicleMainType),
      };

      if (activeFilterType === "spares") {
        params.vehicleCode = "Select";
        params.vehicleRegNo = "Select";
      }

      return params;
    },
    [filterType, transferType, branchType, branchId, loginUserId]
  );

  /* =========================================================
     API LOADERS
  ========================================================= */
  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: { branchId, branchType, loginUserId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setInit({
        years: Array.isArray(data?.years) ? data.years : ["Select"],
        months: Array.isArray(data?.months) ? data.months : ["Select"],
        statusOptions: Array.isArray(data?.statusOptions)
          ? data.statusOptions
          : ["Select"],
        stores: Array.isArray(data?.stores) ? data.stores : [],
        vehicleTypes: Array.isArray(data?.vehicleTypes) ? data.vehicleTypes : [],
        branches: Array.isArray(data?.branches) ? data.branches : [],
        fromLocations: Array.isArray(data?.fromLocations)
          ? data.fromLocations
          : [],
        toLocations: Array.isArray(data?.toLocations) ? data.toLocations : [],
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Init failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [branchId, branchType, loginUserId, commonHeaders, canUse]);

  const loadVehicleDropdowns = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;

    try {
      setDropdownLoading(true);

      const params = buildVehicleDropdownParams();

      const [codeRs, regRs] = await Promise.all([
        axios.get(`${API_BASE}/dropdowns/vehicleCode`, {
          params,
          headers: commonHeaders,
        }),
        axios.get(`${API_BASE}/dropdowns/vehicleRegNo`, {
          params,
          headers: commonHeaders,
        }),
      ]);

      setVehicleCodeDD(
        codeRs?.data?.success && Array.isArray(codeRs?.data?.list)
          ? codeRs.data.list
          : []
      );

      setVehicleRegNoDD(
        regRs?.data?.success && Array.isArray(regRs?.data?.list)
          ? regRs.data.list
          : []
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          "Dropdown load failed"
      );
    } finally {
      setDropdownLoading(false);
    }
  }, [buildVehicleDropdownParams, commonHeaders, loginUserId, canUse]);

  const loadList = useCallback(
    async ({
      silent = false,
      activeForm = form,
      activeFilterType = filterType,
      activeTransferType = transferType,
    } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);
        setChecked({});
        setApprovedBy({});

        const url =
          activeFilterType === "own"
            ? `${API_BASE}/list`
            : `${API_BASE}/list/spares`;

        const { data } = await axios.get(url, {
          params: buildListParams(activeForm, activeFilterType, activeTransferType),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          if (!silent) toast.warn(data?.message || "No data found");
          return;
        }

        const list = Array.isArray(data?.list) ? data.list : [];
        setRows(list);

        if (!silent) {
          toast.success(`Found ${data?.count ?? list.length ?? 0} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        toast.error(
          err?.response?.data?.message || err.message || "Search failed"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [
      form,
      filterType,
      transferType,
      loginUserId,
      branchId,
      buildListParams,
      commonHeaders,
      canUse,
    ]
  );

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      await loadInit();
      await loadVehicleDropdowns();
      await loadList({
        silent: true,
        activeForm: defaultForm,
        activeFilterType: filterType,
        activeTransferType: transferType,
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Page load failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [loadInit, loadVehicleDropdowns, loadList, filterType, transferType]);

  /* ---------------- handlers ---------------- */
  const onFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleFilterTypeChange = useCallback((value) => {
    setFilterType(value);

    if (value === "spares") {
      setForm((prev) => ({
        ...prev,
        vehicleCode: "Select",
        vehicleRegNo: "Select",
      }));
    }
  }, []);

  const handleTransferTypeChange = useCallback((value) => {
    setTransferType(value);

    if (value === "branch") {
      setForm((prev) => ({
        ...prev,
        transferFromLoc: "Select",
        transferToLoc: "Select",
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        transferTo: "Select",
      }));
    }
  }, []);

  const handleSearch = useCallback(async () => {
    await loadList({
      silent: false,
      activeForm: form,
      activeFilterType: filterType,
      activeTransferType: transferType,
    });
  }, [loadList, form, filterType, transferType]);

  const handleViewAll = useCallback(async () => {
    const reset = { ...defaultForm };
    setForm(reset);
    setChecked({});
    setApprovedBy({});
    await loadVehicleDropdowns();
    await loadList({
      silent: false,
      activeForm: reset,
      activeFilterType: filterType,
      activeTransferType: transferType,
    });
  }, [loadVehicleDropdowns, loadList, filterType, transferType]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
  }, [loadPage]);

  const handleClearFilters = useCallback(() => {
    setForm({ ...defaultForm });
    toast.info("Filters cleared");
  }, []);

  const toggleRow = useCallback((rowKey) => {
    setChecked((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  }, []);

  const approveSelected = useCallback(async () => {
    const selectedKeys = Object.keys(checked).filter((k) => checked[k]);

    if (!selectedKeys.length) {
      toast.info("Select at least one row");
      return;
    }

    try {
      setApproveLoading(true);

      const approvals = [];

      rows.forEach((r, idx) => {
        const key = String(getRowKey(r, idx));
        if (!checked[key]) return;

        const by = clean(approvedBy[key]);
        if (!by) return;

        if (filterType === "own") {
          const vehicleTransferId = toInt(
            r?.fld_vehicle_trandferid ?? r?.vehicleTransferId ?? r?.id
          );
          if (vehicleTransferId) {
            approvals.push({ vehicleTransferId, approvedBy: by });
          }
        } else {
          const spareTransferId = toInt(
            r?.fld_spare_trandferid ?? r?.spareTransferId ?? r?.id
          );
          if (spareTransferId) {
            approvals.push({ spareTransferId, approvedBy: by });
          }
        }
      });

      if (!approvals.length) {
        toast.info("Enter Approved By for selected rows");
        return;
      }

      const url =
        filterType === "own"
          ? `${API_BASE}/approve`
          : `${API_BASE}/approve/spares`;

      const payload =
        filterType === "own"
          ? { ipAddress: "", approvals }
          : { approvals };

      const { data } = await axios.post(url, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Approval failed");
      }

      toast.success(data?.message || "Approved successfully");

      await loadList({
        silent: true,
        activeForm: form,
        activeFilterType: filterType,
        activeTransferType: transferType,
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Approval failed"
      );
    } finally {
      setApproveLoading(false);
    }
  }, [
    checked,
    approvedBy,
    rows,
    getRowKey,
    filterType,
    commonHeaders,
    loadList,
    form,
    transferType,
  ]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    loadVehicleDropdowns();
  }, [loadVehicleDropdowns]);

  const recordsCount = useMemo(() => rows.length, [rows]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE TRANSFER APPROVAL
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Transfer Approval
              </div>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Records: {recordsCount}</span>
                <span className={pillClass}>
                  Vehicle Mode: {filterType === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                <span className={pillClass}>
                  Transfer Type: {transferType === "branch" ? "Branch" : "Location"}
                </span>
                <span className={pillClass}>Branch Type: {branchType || "-"}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Vehicle Type / Filter Section */}
          <div className={sectionClass}>
            <div className="mb-6">
              <label className={labelClass}>Vehicle Type</label>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="filterType"
                    value="own"
                    checked={filterType === "own"}
                    onChange={() => handleFilterTypeChange("own")}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Own Vehicle
                  </span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="filterType"
                    value="spares"
                    checked={filterType === "spares"}
                    onChange={() => handleFilterTypeChange("spares")}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Spares & Catalogue
                  </span>
                </label>
              </div>
            </div>

            {showFilters && (
              <div className={sectionClass}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FaFilter /> FILTRATION AREA
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Vehicle Main Type */}
                  <div>
                    <label className={labelClass}>Vehicle Main Type</label>
                    <select
                      name="vehicleMainType"
                      value={form.vehicleMainType}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      <option value="Select">Select</option>
                      {(init.vehicleTypes || []).map((x, i) => (
                        <option
                          key={i}
                          value={String(
                            x?.label ??
                              x?.value ??
                              x?.id ??
                              x?.fld_vehicletypeindex ??
                              "Select"
                          )}
                        >
                          {String(
                            x?.value ??
                              x?.label ??
                              x?.name ??
                              x?.fld_vehicletypename ??
                              "Select"
                          )}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Store */}
                  <div>
                    <label className={labelClass}>Store</label>
                    <select
                      name="store"
                      value={form.store}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      <option value="Select">Select</option>
                      {(init.stores || []).map((x, i) => (
                        <option
                          key={i}
                          value={String(
                            x?.value ?? x?.id ?? x?.fld_storeid ?? "Select"
                          )}
                        >
                          {String(
                            x?.label ?? x?.name ?? x?.fld_storename ?? "Select"
                          )}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Code */}
                  <div>
                    <label className={labelClass}>Vehicle Code</label>
                    <select
                      name="vehicleCode"
                      value={form.vehicleCode}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={filterType === "spares" || isBusy}
                    >
                      <option value="Select">Select</option>
                      {(vehicleCodeDD || []).map((x, i) => (
                        <option
                          key={i}
                          value={String(x?.value ?? x?.id ?? "Select")}
                        >
                          {String(x?.label ?? x?.name ?? "Select")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Number */}
                  <div>
                    <label className={labelClass}>Vehicle Number</label>
                    <select
                      name="vehicleRegNo"
                      value={form.vehicleRegNo}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={filterType === "spares" || isBusy}
                    >
                      <option value="Select">Select</option>
                      {(vehicleRegNoDD || []).map((x, i) => (
                        <option
                          key={i}
                          value={String(x?.value ?? x?.id ?? "Select")}
                        >
                          {String(x?.label ?? x?.name ?? "Select")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className={labelClass}>Status</label>
                    <select
                      name="status"
                      value={form.status}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {(init.statusOptions || ["Select"]).map((s, i) => (
                        <option key={i} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Month */}
                  <div>
                    <label className={labelClass}>Month</label>
                    <select
                      name="month"
                      value={form.month}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {(init.months || ["Select"]).map((m, i) => (
                        <option key={i} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Transfer To */}
                  <div>
                    <label className={labelClass}>Transfer To</label>
                    <select
                      name="transferTo"
                      value={form.transferTo}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={transferType !== "branch" || isBusy}
                    >
                      <option value="Select">Select</option>
                      {(init.branches || []).map((x, i) => (
                        <option
                          key={i}
                          value={String(
                            x?.value ??
                              x?.id ??
                              x?.fld_Branchid ??
                              x?.branchId ??
                              "Select"
                          )}
                        >
                          {String(
                            x?.label ??
                              x?.name ??
                              x?.fld_BranchName ??
                              x?.branchName ??
                              "Select"
                          )}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Transfer Type */}
                  <div>
                    <label className={labelClass}>Transfer Type</label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="transferType"
                          value="branch"
                          checked={transferType === "branch"}
                          onChange={() => handleTransferTypeChange("branch")}
                          disabled={isBusy}
                        />
                        Branch
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="transferType"
                          value="location"
                          checked={transferType === "location"}
                          onChange={() => handleTransferTypeChange("location")}
                          disabled={isBusy}
                        />
                        Location
                      </label>
                    </div>
                  </div>

                  {/* From Location */}
                  <div>
                    <label className={labelClass}>From Location</label>
                    <select
                      name="transferFromLoc"
                      value={form.transferFromLoc}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={transferType !== "location" || isBusy}
                    >
                      <option value="Select">Select</option>
                      {(init.fromLocations || []).map((x, i) => (
                        <option
                          key={i}
                          value={String(
                            x?.value ?? x?.id ?? x?.locationId ?? "Select"
                          )}
                        >
                          {String(
                            x?.label ?? x?.name ?? x?.locationName ?? "Select"
                          )}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* To Location */}
                  <div>
                    <label className={labelClass}>To Location</label>
                    <select
                      name="transferToLoc"
                      value={form.transferToLoc}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={transferType !== "location" || isBusy}
                    >
                      <option value="Select">Select</option>
                      {(init.toLocations || []).map((x, i) => (
                        <option
                          key={i}
                          value={String(
                            x?.value ?? x?.id ?? x?.locationId ?? "Select"
                          )}
                        >
                          {String(
                            x?.label ?? x?.name ?? x?.locationName ?? "Select"
                          )}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Year */}
                  <div>
                    <label className={labelClass}>Year</label>
                    <select
                      name="year"
                      value={form.year}
                      onChange={onFormChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {(init.years || ["Select"]).map((y, i) => (
                        <option key={i} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* action buttons */}
                <div className="flex flex-col md:flex-row gap-3 justify-between items-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleClearFilters}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${
                      isDark
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } font-medium transition-colors duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaUndo />
                    Clear Filters
                  </button>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={handleSearch}
                      disabled={isBusy}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                    >
                      <FaSearch />
                      {gridLoading ? "Searching..." : "Search"}
                    </button>

                    <button
                      onClick={handleViewAll}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                    >
                      <FaEye />
                      View All
                    </button>

                    <button
                      onClick={approveSelected}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
                    >
                      <FaCheck />
                      {approveLoading ? "Approving..." : "Approve Selected"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                Vehicle Transfer Approval List
              </div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle Mode:{" "}
                <span className="font-semibold">
                  {filterType === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                {" • "}
                Records: <span className="font-semibold">{rows.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-center">Sel</th>
                    <th className="px-3 py-3 text-center">S.No</th>
                    <th className="px-3 py-3 text-center">Vehicle Code</th>
                    <th className="px-3 py-3 text-center">Vehicle Number</th>
                    <th className="px-3 py-3 text-center">Date</th>
                    <th className="px-3 py-3 text-center">Transfer From</th>
                    <th className="px-3 py-3 text-center">Transfer To</th>
                    <th className="px-3 py-3 text-center">From Location</th>
                    <th className="px-3 py-3 text-center">To Location</th>
                    <th className="px-3 py-3 text-center">Transfer By</th>
                    <th className="px-3 py-3 text-center">Status</th>
                    <th className="px-3 py-3 text-center">Approved By</th>
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"}>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-4 py-10 text-center text-gray-500">
                        {gridLoading || pageLoading
                          ? "Loading..."
                          : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const key = getRowKey(r, idx);
                      const alreadyApproved = isApprovedRow(r);

                      const statusText =
                        col(r, "Status", "status", "fld_status") ||
                        (alreadyApproved ? "Approved" : "Pending");

                      return (
                        <tr
                          key={key}
                          className={isDark ? "border-t border-gray-800" : "border-t border-gray-100"}
                        >
                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!checked[key]}
                              disabled={alreadyApproved || isBusy}
                              onChange={() => toggleRow(key)}
                            />
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {idx + 1}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {col(r, "fld_vehicle_code", "vehiclecode", "code") || "-"}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {col(r, "fld_vehicle_regno", "vehicleno", "number") || "-"}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {col(r, "fld_vehicle_date", "date") || "-"}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {col(r, "fld_frombranchname", "frombranch", "transferFrom") || "-"}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {col(r, "fld_BranchName", "tobranch", "transferTo") || "-"}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {col(r, "fromlocationname", "fld_fromlocationname", "fromLocation") || "-"}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {col(r, "tolocationname", "fld_tolocationname", "toLocation") || "-"}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {col(r, "fld_username", "transferBy") || "-"}
                          </td>

                          <td
                            className={`px-3 py-3 text-center whitespace-nowrap font-semibold ${
                              String(statusText).toLowerCase().includes("approved")
                                ? "text-green-600"
                                : String(statusText).toLowerCase().includes("dis")
                                ? "text-red-600"
                                : "text-yellow-600"
                            }`}
                          >
                            {statusText}
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="text"
                              value={approvedBy[key] || ""}
                              onChange={(e) =>
                                setApprovedBy((prev) => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                              className={`px-2 py-1 rounded border text-sm w-36 ${
                                isDark
                                  ? "bg-gray-800 border-gray-700 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                              placeholder="Approved By"
                              disabled={alreadyApproved || !checked[key] || isBusy}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {isBusy && (
              <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {pageLoading
                  ? "Loading page..."
                  : dropdownLoading
                  ? "Loading dropdown..."
                  : gridLoading
                  ? "Loading grid..."
                  : approveLoading
                  ? "Processing approval..."
                  : "Loading..."}
              </div>
            )}
          </div>

          {/* Back */}
          <div className="flex justify-start mt-6">
            <button
              onClick={handleBack}
              disabled={isBusy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTransferApproval;